import { forwardRef, useCallback, useRef } from "react";
import MathKeyboard from "../../VirtualMathKeyboard/MathKeyboard";
import { Check, CloseOutlined } from "@mui/icons-material";
import { useDragAndResize } from "../hooks/useDragAndResize";
import { useBoxStyling } from "../hooks/useBoxStyles";
import { AdminControls } from "./AdminControls";

const TextInput = ({ value, onChange, styles, readOnly, onBlur }) => (
  <input
    name="textAnswer"
    type="text"
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    onBlur={onBlur}
    className="text-input"
    placeholder="Type answer..."
    style={{ fontSize: styles.size, color: styles.color }}

    readOnly={readOnly}
  />
);

const SelectInput = ({ onChange, box, isExerciseAttempted, showAnswers }) => {
  const isCorrect = box?.isAnswerCorrect === true;
  const isIncorrect = box?.isAnswerCorrect === false;
  const isUnselected = isExerciseAttempted
    ? box?.isAnswerCorrect === "unselected"
    : null;

  const getSelectClassName = () => {
    const classes = ["select-input"];

    if (isExerciseAttempted && box.answer === null) {
      if (isCorrect) classes.push("correct");
      else if (isIncorrect) classes.push("incorrect");
      else if (isUnselected) {
        classes.push("unselected");
      }
    } else if (box.answer) {
      classes.push("attempted");
    }

    return classes.join(" ");
  };

  return (
    <div
      className={getSelectClassName()}
      onClick={() =>
        onChange(
          box.answer === null && isExerciseAttempted && box.userSubmittedAnswer
            ? false
            : !box.answer
        )
      }
    >
      {box.answer === null && isCorrect && <Check className="check-icon" />}
      {!isUnselected && !showAnswers && box.answer === null && isIncorrect && (
        <CloseOutlined className="check-icon" />
      )}
      {!showAnswers && box.answer === null && isUnselected && (
        <CloseOutlined className="check-icon" />
      )}
      {box.answer && <Check className="check-icon" />}
    </div>
  );
};

const MathInput = forwardRef(
  (
    { value, onChange, onSelect, onBlur, showAnswers, styles, boxId },
    mathKeyboardRef
  ) => {
    return (
      <MathKeyboard
        ref={mathKeyboardRef}
        value={value || ""}
        onChange={onChange}
        onClick={() => onSelect(boxId)}
        onBlur={onBlur}
        readonly={showAnswers}
        placeholder="Math input"
        className="math-keyboard"
        blockPhysicalKeyboard={false}
        options={{
          style: {
            height: "100%",
            width: "100%",
            outline: "none",
            border: "none",
            backgroundColor: "transparent",
            padding: "0px",
            fontSize: styles.size,
            color: styles.color,
          },
        }}
      />
    );
  }
);

const InputRenderer = forwardRef(
  (
    {
      box,
      answer,
      onAnswerChange,
      onBlur,
      onSelect,
      isExerciseAttempted,
      showAnswers,
      styles,
    },
    ref
  ) => {
    switch (box.type) {
      case "text":
        return (
          <TextInput
            value={answer}
            onChange={onAnswerChange}
            onBlur={() => onBlur(box)}
            styles={styles}
            readOnly={showAnswers}
          />
        );
      case "descriptive":
        return (
          <textarea
            name="descriptiveAnswer"
            value={answer || ""}
            onChange={(e) => onAnswerChange(e.target.value)}
            onBlur={() => onBlur(box)}
            style={{ fontSize: styles.size, color: styles.color }}
            placeholder="Type your answer..."
            readOnly={showAnswers}
            className="text-input"
          />
        );
      case "single-select":
      case "multi-select":
        return (
          <SelectInput
            onChange={onAnswerChange}
            box={box}
            isExerciseAttempted={isExerciseAttempted}
            showAnswers={showAnswers}
          />
        );
      case "math":
        return (
          <MathInput
            ref={ref}
            value={answer}
            onChange={onAnswerChange}
            onSelect={onSelect}
            onBlur={() => onBlur(box)}
            showAnswers={showAnswers}
            styles={styles}
            boxId={box.id}
          />
        );
      default:
        return null;
    }
  }
);

export const InputBox = ({
  box,
  isAdmin,
  isSelected,
  onSelect,
  onUpdate,
  onDelete,
  onBlur,
  onCopy,
  containerRef,
  showAnswers,
  isExerciseAttempted,
  isPx,
  styles = {
    size: "16px",
    color: "black",
  },
}) => {
  const boxRef = useRef(null);
  const mathKeyboardRef = useRef(null);

  const { isDragging, handleMouseDown } = useDragAndResize(
    box,
    onUpdate,
    isAdmin,
    containerRef,
    onSelect
  );

  const { className, style } = useBoxStyling(
    box,
    isSelected,
    isAdmin,
    isDragging,
    isExerciseAttempted,
    showAnswers,
    isPx
  );

  const handleAnswerChange = useCallback(
    (value) => {
      onUpdate(box.id, { answer: value });
    },
    [box.id, onUpdate]
  );

  const answer = showAnswers
    ? box.correctAnswer
    : isExerciseAttempted && box.answer === null
      ? box.userSubmittedAnswer
      : box.answer;

  return (
    <div
      ref={boxRef}
      className={className}
      style={style}
      onMouseDown={(e) => handleMouseDown(e, "drag")}
      onClick={() => {
        if (!showAnswers) {
          if (box.type === "math" && mathKeyboardRef.current) {
            mathKeyboardRef.current.showKeyboard();
            mathKeyboardRef.current.focus();
          }
          onSelect(box.id);
        }
      }}
    >
      <InputRenderer
        box={box}
        answer={answer}
        onAnswerChange={handleAnswerChange}
        onBlur={onBlur}
        onSelect={onSelect}
        isExerciseAttempted={isExerciseAttempted}
        showAnswers={showAnswers}
        styles={styles}
        ref={mathKeyboardRef}
      />

      {isAdmin && (
        <AdminControls
          isSelected={isSelected}
          onCopy={onCopy}
          onDelete={onDelete}
          onResizeMouseDown={(e) => handleMouseDown(e, "resize")}
          boxId={box.id}
        />
      )}
    </div>
  );
};
